/**
 * @typedef {import("../types.js").TranscriptSettings} TranscriptSettings
 */

import jsPDF from "jspdf";
import { insureTvLogo, insureTvStripe } from "./insure-tv-logo.js";
import { assetTvLogo, assetTvStripe } from "./atv-logo.js";

// Document settings.
// Top and bottom margins.
let headY = 24;
let footY = 20;
// The document side margins.
let marginX = 20;
let normalLineHeight = 6;
let smallFontSize = 8;
let headingFontSize = 18;
let paragraphFontSize = 11;

// The current y position of content in the document.
let y = headY;

// Default theme to Asset TV blue.
let brandColour = [2, 132, 199];
let logo = assetTvLogo;
let stripe = assetTvStripe;
let imageWidth = 12 * 2.93; // Work out the width from desired height.

/**
 * Sets theme variable from theme name.
 * @param {string} variant - Variant name, "insure" or "assettv".
 */
function setVariant(variant) {
  // Just in case we're not using defaults.
  brandColour = [2, 132, 199];
  logo = assetTvLogo;
  stripe = assetTvStripe;
  imageWidth = 12 * 2.93;

  if (variant === "insure") {
    // Use Insure TV yellow.
    brandColour = [255, 187, 2];
    logo = insureTvLogo;
    stripe = insureTvStripe;
    imageWidth = 12 * 2.89;
  }
}

/**
 * Break a section of text into lines, used for jsPDF where need to include
 * line breaks so the text doesn't run off page.
 * @param {jsPDF} doc - The document
 * @param {string} text - Text to add to document text style should have been defined up-front.
 * @param {number} lineheight - Line height to use.
 */
function outputText(doc, text, lineheight = normalLineHeight) {
  if (text === null) {
    return;
  }

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const textLines = doc.splitTextToSize(text.trim(), pageWidth - marginX * 2);

  textLines.forEach((line) => {
    if (y + lineheight > pageHeight - footY) {
      doc.addPage();
      doc.addImage(stripe, "PNG", 0, 0, doc.internal.pageSize.getWidth(), 4);
      y = headY;
    }

    doc.text(line, marginX, (y += lineheight));
  });
}

/**
 * Generate a jsPDF transcript document.
 * @param {TranscriptSettings} settings - Drupal settings object for generate transcript.
 * @returns {jsPDF|void} - jsPDF document with transcript. Nothing if unable to find transcript content in document.
 */
export function generatePdfTranscript(settings) {
  // Reset y position.
  y = headY;

  setVariant(settings.variant);

  const transcriptElement = document.querySelector("#video-transcript-body");

  if (!(transcriptElement instanceof HTMLElement)) {
    return;
  }

  const videoTitle = settings.title;
  const companies = settings.companies;
  const people = settings.people;

  const doc = new jsPDF();
  const font = "helvetica";

  doc.addImage(stripe, "PNG", 0, 0, doc.internal.pageSize.getWidth(), 4);
  doc.addImage(
    logo,
    "PNG",
    doc.internal.pageSize.getWidth() - 8 - imageWidth,
    12,
    imageWidth,
    12,
  );

  doc.setFont(font, "bold");
  doc.setFontSize(smallFontSize);
  doc.setTextColor(...brandColour);
  doc.text("VIDEO TRANSCRIPT", marginX, (y += 10));

  doc.setFontSize(headingFontSize);
  doc.setTextColor(0, 0, 0);
  outputText(doc, videoTitle, 10);

  doc.setTextColor(113, 113, 122);
  doc.setFontSize(paragraphFontSize);

  doc.text("Companies:", marginX, (y += 15));
  doc.setFont(font, "normal");
  outputText(doc, companies);

  doc.setFont(font, "bold");
  doc.text("People:", marginX, (y += 8));
  doc.setFont(font, "normal");
  outputText(doc, people);

  const paragraphs = transcriptElement.querySelectorAll("p");
  y += 6;

  paragraphs.forEach((paragraph) => {
    const breakPosition = paragraph.textContent.indexOf(":");

    if (breakPosition > 50) {
      // If the first ":" is over 50 characters in it's unlikely to be the
      // speaker name.
      breakPosition = -1;
    }

    if (breakPosition !== -1) {
      const speakerName = paragraph.textContent.substring(0, breakPosition + 1);
      doc.setFont(font, "bold");
      doc.text(speakerName, marginX, (y += normalLineHeight));
    }

    const text = paragraph.textContent.substring(breakPosition + 1);
    doc.setFont(font, "normal");
    outputText(doc, text);

    y += 4;
  });

  return doc;
}
